import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Bell, 
  Users, 
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Volume2,
  VolumeX,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FoodPantryNowServingPage = () => {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [nowServing, setNowServing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Alert dialog
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [alertMessage, setAlertMessage] = useState("Please meet us at the door to have assistance taking your box to your car");
  const [sendingAlert, setSendingAlert] = useState(false);

  const fetchQueue = useCallback(async (showLoader = true) => {
    if (showLoader) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/food-pantry/queue?location_id=fp-nashville&date=${new Date().toISOString().split('T')[0]}`);
      // Filter to only show waiting/ready clients
      const activeQueue = (response.data.checkins || []).filter(c => 
        !['Completed', 'Cancelled'].includes(c.queue_status)
      );
      setQueue(activeQueue);
      
      // Fetch current now serving status
      const servingResponse = await axios.get(`${API}/food-pantry/now-serving/fp-nashville`);
      setNowServing(servingResponse.data.now_serving || null);
    } catch (error) {
      console.error('Failed to fetch queue:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(() => fetchQueue(false), 15000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  const handleCallClient = (client) => {
    setSelectedClient(client);
    setAlertMessage("Please meet us at the door to have assistance taking your box to your car");
    setShowAlertDialog(true);
  };

  const handleSendAlert = async () => {
    if (!selectedClient) return;
    
    setSendingAlert(true);
    try {
      await axios.post(`${API}/food-pantry/now-serving`, {
        location_id: 'fp-nashville',
        checkin_id: selectedClient.id,
        queue_number: selectedClient.queue_number,
        message: alertMessage
      });
      
      toast.success(`Now serving #${selectedClient.queue_number}`);
      setShowAlertDialog(false);
      setNowServing({
        queue_number: selectedClient.queue_number,
        message: alertMessage,
        checkin_id: selectedClient.id
      });
      fetchQueue(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send alert');
    } finally {
      setSendingAlert(false);
    }
  };

  const handleClearAlert = async () => {
    try {
      await axios.delete(`${API}/food-pantry/now-serving/fp-nashville`);
      setNowServing(null);
      toast.success('Alert cleared');
    } catch (error) {
      toast.error('Failed to clear alert');
    }
  };

  const handleMarkCompleted = async (client) => {
    try {
      await axios.put(`${API}/food-pantry/queue/${client.id}`, {
        queue_status: 'Completed',
        box_distributed: true
      });
      toast.success(`Marked #${client.queue_number} as completed`);
      
      // Clear now serving if this was the one being served
      if (nowServing && nowServing.checkin_id === client.id) {
        await handleClearAlert();
      }
      
      fetchQueue(false);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (client) => {
    const status = client.queue_status || client.eligibility_status || 'In Review';
    
    if (status.includes('Eligible') || status.includes('Created')) {
      return <Badge className="bg-green-500">Ready</Badge>;
    }
    if (status.includes('Waiting')) {
      return <Badge className="bg-blue-500">Waiting</Badge>;
    }
    if (status.includes('New Client')) {
      return <Badge className="bg-purple-500">New Client</Badge>;
    }
    return <Badge className="bg-yellow-500">In Review</Badge>;
  };

  const openLobbyDisplay = () => {
    window.open('/lobby-display', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6" data-testid="now-serving-control-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Now Serving Control</h1>
          <p className="text-muted-foreground">Nashville Food Pantry - Call clients from the queue</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openLobbyDisplay}>
            <ExternalLink className="h-4 w-4 mr-2" />
            Open Lobby Display
          </Button>
          <Button variant="outline" onClick={() => fetchQueue()} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Current Now Serving Status */}
      {nowServing && (
        <Card className="border-green-500 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-green-500 text-white">
                  <Bell className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-sm text-green-600 font-medium">CURRENTLY DISPLAYING</p>
                  <p className="text-3xl font-bold text-green-700">Now Serving #{nowServing.queue_number}</p>
                  <p className="text-green-600">{nowServing.message}</p>
                </div>
              </div>
              <Button variant="outline" onClick={handleClearAlert} className="border-green-500 text-green-700">
                <VolumeX className="h-4 w-4 mr-2" />
                Clear Alert
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Queue Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{queue.length}</p>
                <p className="text-sm text-muted-foreground">In Queue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{queue.filter(c => c.queue_status?.includes('Eligible') || c.queue_status?.includes('Created')).length}</p>
                <p className="text-sm text-muted-foreground">Ready</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-8 w-8 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold">{queue.filter(c => c.queue_status?.includes('Review') || c.queue_status?.includes('Verification')).length}</p>
                <p className="text-sm text-muted-foreground">In Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{queue.filter(c => c.client_type === 'new').length}</p>
                <p className="text-sm text-muted-foreground">New Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Queue List */}
      <Card>
        <CardHeader>
          <CardTitle>Client Queue</CardTitle>
          <CardDescription>Click "Call" to display the client on the lobby screen</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : queue.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No clients in queue</p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {queue.map((client) => {
                  const isCurrentlyServing = nowServing && nowServing.checkin_id === client.id;
                  
                  return (
                    <div
                      key={client.id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        isCurrentlyServing ? 'bg-green-50 border-green-300' : 'bg-muted/30'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`text-2xl font-bold ${isCurrentlyServing ? 'text-green-600' : 'text-primary'}`}>
                          #{client.queue_number}
                        </div>
                        <div>
                          <p className="font-medium">
                            {client.first_name?.charAt(0)}. {client.last_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Checked in at {client.checkin_time?.substring(0, 5)}
                          </p>
                        </div>
                        {getStatusBadge(client)}
                        {isCurrentlyServing && (
                          <Badge variant="outline" className="border-green-500 text-green-600">
                            <Bell className="h-3 w-3 mr-1" />
                            On Display
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCallClient(client)}
                          disabled={isCurrentlyServing}
                        >
                          <Volume2 className="h-4 w-4 mr-1" />
                          Call
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleMarkCompleted(client)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Complete
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Call Client Dialog */}
      <Dialog open={showAlertDialog} onOpenChange={setShowAlertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Call Client #{selectedClient?.queue_number}</DialogTitle>
            <DialogDescription>
              This will display an alert on the lobby screen
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Client</p>
              <p className="text-lg font-medium">
                {selectedClient?.first_name?.charAt(0)}. {selectedClient?.last_name}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Display Message</Label>
              <Textarea
                id="message"
                value={alertMessage}
                onChange={(e) => setAlertMessage(e.target.value)}
                placeholder="Enter the message to display..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlertDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendAlert} disabled={sendingAlert} className="bg-green-600 hover:bg-green-700">
              {sendingAlert ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Bell className="h-4 w-4 mr-2" />
              )}
              Display Now Serving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FoodPantryNowServingPage;
